import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2, X, ZoomIn, ZoomOut, Download, ExternalLink, Maximize2, FileText } from "lucide-react";
import { downloadUrl, extOf, OFFICE_EXT, previewPdfUrl } from "@/lib/uploads";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(Math.floor(el.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const Spinner = ({ label = "Caricamento PDF…" }) => (
  <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" data-testid="pdf-loading">
    <Loader2 className="w-4 h-4 animate-spin" /> {label}
  </div>
);

const ErrorBox = ({ href }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground" data-testid="pdf-error">
    <FileText className="w-6 h-6 text-gold" />
    <p>Anteprima non disponibile.</p>
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-gold underline">Apri il PDF in una nuova scheda</a>
  </div>
);

// Anteprima inline (prima pagina) cliccabile: apre il visualizzatore a schermo intero
export function PdfPreview({ href, name, onOpen, testid = "pdf-preview" }) {
  const [ref, width] = useContainerWidth();
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(false);
  return (
    <div ref={ref} data-testid={testid} className="relative w-full">
      {error ? <ErrorBox href={href} /> : (
        <button type="button" onClick={onOpen} data-testid={`${testid}-open`} title={`Apri ${name || "PDF"}`}
          className="group relative block w-full text-left rounded-xl overflow-hidden border border-white/10 bg-[#0B1220] cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
          <div className="max-h-[70vh] overflow-hidden">
            {width > 0 && (
              <Document file={href} loading={<Spinner />} onLoadSuccess={({ numPages: n }) => setNumPages(n)} onLoadError={() => setError(true)} error={<ErrorBox href={href} />}>
                <Page pageNumber={1} width={width} renderTextLayer={false} renderAnnotationLayer={false} loading={<Spinner label="Rendering pagina…" />} />
              </Document>
            )}
          </div>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#020617] via-[#020617]/80 to-transparent pt-12 pb-3 px-4 flex items-end justify-between gap-3">
            <span className="text-xs text-muted-foreground truncate">{name}</span>
            <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-[#020617] bg-gold group-hover:bg-[#FDE047] rounded-full px-3 py-1.5 transition-colors">
              <Maximize2 className="w-3.5 h-3.5" /> Apri{numPages ? ` · ${numPages} pag.` : ""}
            </span>
          </div>
        </button>
      )}
    </div>
  );
}

// Visualizzatore a schermo intero con tutte le pagine scorrevoli e zoom
export function PdfViewerDialog({ href, name, open, onClose, downloadHref }) {
  if (!open) return null;
  return <PdfViewerInner href={href} name={name} onClose={onClose} downloadHref={downloadHref || href} />;
}

// Stato anteprima di un allegato: PDF nativo, PDF generato dal server (Office) oppure fallback viewer Microsoft
export function useDocPreview(href, name) {
  const ext = extOf(name || href || "");
  const isPdf = ext === "pdf";
  const isOffice = OFFICE_EXT.includes(ext);
  const [state, setState] = useState(() => (isPdf ? { status: "ready", pdfUrl: href } : isOffice ? { status: "checking", pdfUrl: null } : { status: "none", pdfUrl: null }));
  useEffect(() => {
    if (!isOffice) return undefined;
    const url = previewPdfUrl(href);
    if (!url) { setState({ status: "fallback", pdfUrl: null }); return undefined; }
    let alive = true;
    setState({ status: "checking", pdfUrl: null });
    fetch(url, { method: "GET" })
      .then((r) => { if (alive) setState(r.ok ? { status: "ready", pdfUrl: url } : { status: "fallback", pdfUrl: null }); })
      .catch(() => { if (alive) setState({ status: "fallback", pdfUrl: null }); });
    return () => { alive = false; };
  }, [href, isOffice]);
  if (isPdf) return { ext, isPdf, isOffice, previewable: true, status: "ready", pdfUrl: href };
  return { ext, isPdf, isOffice, previewable: isOffice, status: isOffice ? state.status : "none", pdfUrl: isOffice ? state.pdfUrl : null };
}

// Visualizzatore di fallback (Microsoft Office Online) quando il server non ha generato il PDF
export function OfficeOnlineDialog({ href, name, open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);
  if (!open) return null;
  const src = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(href)}`;
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-[#020617] flex flex-col" data-testid="office-viewer" role="dialog" aria-modal="true">
      <header className="flex items-center gap-2 px-3 sm:px-4 h-14 border-b border-white/10 bg-[#0B1220] shrink-0">
        <FileText className="w-4 h-4 text-gold shrink-0" />
        <p className="text-sm font-semibold truncate flex-1 min-w-0" title={name}>{name}</p>
        <a data-testid="office-viewer-download" href={downloadUrl(href)} download={name} title="Scarica" className="w-9 h-9 rounded-full bg-gold text-[#020617] hover:bg-[#FDE047] flex items-center justify-center transition-colors"><Download className="w-4 h-4" /></a>
        <button type="button" data-testid="office-viewer-close" onClick={onClose} title="Chiudi" className="w-9 h-9 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center transition-colors ml-1"><X className="w-4 h-4" /></button>
      </header>
      <iframe title={name || "Documento"} src={src} className="flex-1 w-full bg-white" allowFullScreen />
    </div>,
    document.body
  );
}

// Dialog unico: sceglie il visualizzatore giusto in base allo stato dell'anteprima
export function DocViewerDialog({ href, name, pdfUrl, open, onClose }) {
  if (pdfUrl) return <PdfViewerDialog href={pdfUrl} name={name} open={open} onClose={onClose} downloadHref={href} />;
  return <OfficeOnlineDialog href={href} name={name} open={open} onClose={onClose} />;
}

function PdfViewerInner({ href, name, onClose, downloadHref }) {
  const [ref, width] = useContainerWidth();
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [current, setCurrent] = useState(1);
  const [error, setError] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const pages = el.querySelectorAll("[data-pdf-page]");
    let best = 1, bestDist = Infinity;
    const mid = el.getBoundingClientRect().top + el.clientHeight / 3;
    pages.forEach((p) => {
      const r = p.getBoundingClientRect();
      const d = Math.abs(r.top - mid);
      if (r.bottom > el.getBoundingClientRect().top && d < bestDist) { bestDist = d; best = Number(p.getAttribute("data-pdf-page")); }
    });
    setCurrent(best);
  }, []);

  const pageWidth = Math.max(200, Math.min(width - 24, 980) * zoom);

  // Pinch-to-zoom e doppio tocco su mobile
  const pinch = useRef({ dist: 0, zoom: 1, lastTap: 0 });
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinch.current.dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      pinch.current.zoom = zoom;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - pinch.current.lastTap < 300) setZoom((z) => (z > 1.05 ? 1 : 1.8));
      pinch.current.lastTap = now;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length !== 2 || !pinch.current.dist) return;
    e.preventDefault();
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    const next = Math.min(3, Math.max(0.5, pinch.current.zoom * (d / pinch.current.dist)));
    setZoom(+next.toFixed(2));
  };
  const onTouchEnd = () => { pinch.current.dist = 0; };
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-[#020617] flex flex-col" data-testid="pdf-viewer" role="dialog" aria-modal="true" aria-label={name || "PDF"}>
      <header className="flex items-center gap-2 px-3 sm:px-4 h-14 border-b border-white/10 bg-[#0B1220] shrink-0">
        <FileText className="w-4 h-4 text-gold shrink-0" />
        <p className="text-sm font-semibold truncate flex-1 min-w-0" title={name}>{name || "Documento PDF"}</p>
        <span className="hidden sm:inline text-xs text-muted-foreground tabular-nums shrink-0" data-testid="pdf-viewer-page-indicator">{numPages ? `Pag. ${current} / ${numPages}` : ""}</span>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" data-testid="pdf-viewer-zoom-out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)))} title="Riduci" className="w-9 h-9 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold flex items-center justify-center transition-colors"><ZoomOut className="w-4 h-4" /></button>
          <span className="hidden sm:inline text-xs w-11 text-center tabular-nums text-muted-foreground">{Math.round(zoom * 100)}%</span>
          <button type="button" data-testid="pdf-viewer-zoom-in" onClick={() => setZoom((z) => Math.min(3, +(z + 0.2).toFixed(2)))} title="Ingrandisci" className="w-9 h-9 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold flex items-center justify-center transition-colors"><ZoomIn className="w-4 h-4" /></button>
          <a data-testid="pdf-viewer-external" href={href} target="_blank" rel="noopener noreferrer" title="Apri in una nuova scheda" className="hidden sm:flex w-9 h-9 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold items-center justify-center transition-colors"><ExternalLink className="w-4 h-4" /></a>
          <a data-testid="pdf-viewer-download" href={downloadUrl(downloadHref || href)} download={name} title="Scarica" className="w-9 h-9 rounded-full bg-gold text-[#020617] hover:bg-[#FDE047] flex items-center justify-center transition-colors"><Download className="w-4 h-4" /></a>
          <button type="button" data-testid="pdf-viewer-close" onClick={onClose} title="Chiudi" className="w-9 h-9 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center transition-colors ml-1"><X className="w-4 h-4" /></button>
        </div>
      </header>
      <div ref={(el) => { ref.current = el; scrollRef.current = el; }} onScroll={onScroll} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        className="flex-1 overflow-auto overscroll-contain px-3 py-4" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}>
        {error ? <ErrorBox href={href} /> : width > 0 && (
          <Document file={href} loading={<Spinner />} onLoadSuccess={({ numPages: n }) => setNumPages(n)} onLoadError={() => setError(true)} error={<ErrorBox href={href} />}>
            <div className="flex flex-col items-center gap-4 min-w-fit mx-auto">
              {Array.from({ length: numPages || 0 }, (_, i) => (
                <div key={i} data-pdf-page={i + 1} className="shadow-2xl shadow-black/60 bg-white">
                  <Page pageNumber={i + 1} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} loading={<div style={{ width: pageWidth, height: pageWidth * 1.414 }} className="bg-white/90 animate-pulse" />} />
                </div>
              ))}
            </div>
          </Document>
        )}
      </div>
      {numPages && (
        <div className="sm:hidden absolute bottom-3 left-1/2 -translate-x-1/2 text-xs bg-[#0B1220]/90 border border-white/10 rounded-full px-3 py-1 tabular-nums text-muted-foreground" data-testid="pdf-viewer-page-indicator-mobile">Pag. {current} / {numPages} · {Math.round(zoom * 100)}%</div>
      )}
    </div>,
    document.body
  );
}
